import Phaser from 'phaser'

const sharedInstance = new Phaser.Events.EventEmitter()
const LOGO_OVERLAY_VISIBILITY_EVENT = 'logo-overlay-visibility-changed'

type PersistentBudgetState = {
	current: number
	max: number
	hasCardPowerup?: boolean
}

type PersistentPlayTimeState = {
	elapsedMs: number
}

type PersistentRankingRunSession = {
	runId: string
	runToken: string
	startedAtClientMs: number
	seq: number
	finished: boolean
	levelIndex: number
	mapKey: string
}

type PersistentGameOverSummary = {
	nickname: string
	finalBudget: number
	finalMaxBudget: number
	elapsedMs: number
	computedScore?: number
	dailyPosition?: number | null
	integrityStatus?: string
	flags?: string[]
	runId?: string
	publicRunCode?: string
	rankingRows?: Array<{
		position: number
		nickname: string
		score: number
		durationMs: number
	}>
}

let persistentBudgetState: PersistentBudgetState | undefined
let persistentPlayTimeState: PersistentPlayTimeState | undefined
let persistentRankingRunSession: PersistentRankingRunSession | undefined
let persistentGameOverSummary: PersistentGameOverSummary | undefined

const setLogoOverlayVisible = (isVisible: boolean) => {
	sharedInstance.emit(LOGO_OVERLAY_VISIBILITY_EVENT, isVisible)
}

const setPersistentBudgetState = (state: PersistentBudgetState) => {
	persistentBudgetState = {
		current: state.current,
		max: state.max,
		hasCardPowerup: state.hasCardPowerup === true
	}
}

const getPersistentBudgetState = (): PersistentBudgetState | undefined => {
	if (!persistentBudgetState)
	{
		return undefined
	}

	return {
		current: persistentBudgetState.current,
		max: persistentBudgetState.max,
		hasCardPowerup: persistentBudgetState.hasCardPowerup === true
	}
}

const clearPersistentBudgetState = () => {
	persistentBudgetState = undefined
}

const setPersistentPlayTimeState = (state: PersistentPlayTimeState) => {
	persistentPlayTimeState = {
		elapsedMs: Math.max(0, Math.floor(state.elapsedMs))
	}
}

const getPersistentPlayTimeState = (): PersistentPlayTimeState | undefined => {
	if (!persistentPlayTimeState)
	{
		return undefined
	}

	return {
		elapsedMs: persistentPlayTimeState.elapsedMs
	}
}

const clearPersistentPlayTimeState = () => {
	persistentPlayTimeState = undefined
}

const setPersistentRankingRunSession = (session: PersistentRankingRunSession) => {
	persistentRankingRunSession = {
		runId: session.runId,
		runToken: session.runToken,
		startedAtClientMs: Math.max(0, Math.floor(session.startedAtClientMs)),
		seq: Math.max(0, Math.floor(session.seq)),
		finished: session.finished === true,
		levelIndex: Math.max(0, Math.floor(session.levelIndex)),
		mapKey: session.mapKey
	}
}

const getPersistentRankingRunSession = (): PersistentRankingRunSession | undefined => {
	if (!persistentRankingRunSession)
	{
		return undefined
	}

	return {
		runId: persistentRankingRunSession.runId,
		runToken: persistentRankingRunSession.runToken,
		startedAtClientMs: persistentRankingRunSession.startedAtClientMs,
		seq: persistentRankingRunSession.seq,
		finished: persistentRankingRunSession.finished,
		levelIndex: persistentRankingRunSession.levelIndex,
		mapKey: persistentRankingRunSession.mapKey
	}
}

const clearPersistentRankingRunSession = () => {
	persistentRankingRunSession = undefined
}

const setPersistentGameOverSummary = (summary: PersistentGameOverSummary) => {
	persistentGameOverSummary = {
		...summary,
		elapsedMs: Math.max(0, Math.floor(summary.elapsedMs)),
		finalBudget: Math.max(0, Math.floor(summary.finalBudget)),
		finalMaxBudget: Math.max(0, Math.floor(summary.finalMaxBudget))
	}
}

const getPersistentGameOverSummary = (): PersistentGameOverSummary | undefined => {
	if (!persistentGameOverSummary)
	{
		return undefined
	}

	return {
		...persistentGameOverSummary,
		rankingRows: persistentGameOverSummary.rankingRows?.map(row => ({ ...row }))
	}
}

const clearPersistentGameOverSummary = () => {
	persistentGameOverSummary = undefined
}

export {
	sharedInstance,
	LOGO_OVERLAY_VISIBILITY_EVENT,
	setLogoOverlayVisible,
	setPersistentBudgetState,
	getPersistentBudgetState,
	clearPersistentBudgetState,
	setPersistentPlayTimeState,
	getPersistentPlayTimeState,
	clearPersistentPlayTimeState,
	setPersistentRankingRunSession,
	getPersistentRankingRunSession,
	clearPersistentRankingRunSession,
	setPersistentGameOverSummary,
	getPersistentGameOverSummary,
	clearPersistentGameOverSummary
}

export type {
	PersistentRankingRunSession,
	PersistentGameOverSummary
}
